import {
  getCollectionLabel,
  getDataPointForMarkerName,
  getDataPointsForTest,
  getTestsForDataPoint,
  type BiomarkerTestingMethod,
  type ContentTag,
  type DataPointSampleType,
} from "@src/packages/content";
import type { Locale } from "@src/packages/i18n/locale";
import { normalizeBiomarkerName } from "../biomarker-identity";

type BiomarkerFactsheet = {
  description: string;
  whyItMatters: string;
  normalRange: string;
  /** Optional scannable guidance for ranges that need more context than a paragraph. */
  normalRangeBullets?: string[];
  /** Canonical testing-content metadata used by result factsheets. */
  subtitle?: string;
  tags?: ContentTag[];
  icon?: string;
  sampleTypes?: DataPointSampleType[];
  testingMethods?: BiomarkerTestingMethod[];
  /** Optional technical context for the advanced Biology tab. */
  biology?: string;
  measurementDetails?: string;
  biologicalVariation?: string;
  interpretation?: string;
  howItWorks?: string;
  testingTiming?: string;
  relatedMarkers?: string[];
};

type BiomarkerFactsheetCopy = Partial<
  Pick<
    BiomarkerFactsheet,
    | "description"
    | "whyItMatters"
    | "normalRange"
    | "normalRangeBullets"
    | "biology"
    | "measurementDetails"
    | "biologicalVariation"
  >
>;

const AMH_NORMAL_RANGE =
  "Age-specific. There is no single normal range for everyone; compare your result with the age- and assay-specific reference range from the laboratory that ran the test.";

const AMH_NORMAL_RANGE_BULLETS = [
  "Approximate median values (Beckman assay): age 25 ~3.3, age 30 ~2.5, age 35 ~1.4, age 40 ~0.5, and age 42 ~0.3 ng/mL.",
  "Roche Elecsys may read about 23% lower than Beckman.",
  "Always compare your result with the age- and assay-specific range from the laboratory that ran the test.",
];

const BIOMARKER_FACTSHEETS: Record<string, BiomarkerFactsheet> = {
  "Estradiol (E2)": {
    description:
      "Estradiol (E2) is the primary estrogen produced by growing ovarian follicles. It's the most important hormone tracked during stimulation.",
    whyItMatters:
      "Rising E2 levels confirm that follicles are growing and responding to medication. Your clinic uses E2 alongside ultrasound to decide when to trigger.",
    normalRange:
      "Day 2–4 baseline: <80 pg/mL. During stim: 200–4000 pg/mL typical (~200–300 per mature follicle). >4000 flags OHSS risk (ESHRE 2019, Bosch 2003).",
  },
  E2: {
    description:
      "Estradiol (E2) is the primary estrogen produced by growing ovarian follicles. It's the most important hormone tracked during stimulation.",
    whyItMatters:
      "Rising E2 levels confirm that follicles are growing and responding to medication. Your clinic uses E2 alongside ultrasound to decide when to trigger.",
    normalRange:
      "Day 2–4 baseline: <80 pg/mL. During stim: 200–4000 pg/mL typical (~200–300 per mature follicle). >4000 flags OHSS risk (ESHRE 2019, Bosch 2003).",
  },
  Estradiol: {
    description:
      "Estradiol (E2) is the primary estrogen produced by growing ovarian follicles. It's the most important hormone tracked during stimulation.",
    whyItMatters:
      "Rising E2 levels confirm that follicles are growing and responding to medication. Your clinic uses E2 alongside ultrasound to decide when to trigger.",
    normalRange:
      "Day 2–4 baseline: <80 pg/mL. During stim: 200–4000 pg/mL typical (~200–300 per mature follicle). >4000 flags OHSS risk (ESHRE 2019, Bosch 2003).",
  },
  LH: {
    description:
      "Luteinizing hormone (LH) triggers ovulation. During IVF, your clinic monitors LH to prevent a premature surge before retrieval.",
    whyItMatters:
      "A premature LH surge can cause early ovulation, which would cancel the cycle. GnRH antagonist medications are used to suppress it.",
    normalRange:
      "Day 2–4 baseline: 1–15 IU/L. Mid-cycle (day 12–16): ≥15 = surge (ovulation within 24–36 h). Luteal (day 17+): <15 typical. Elevated baseline with LH:FSH >2 suggests PCOS pattern (ASRM 2022, Rotterdam 2003).",
  },
  "Endometrial Thickness": {
    description:
      "The endometrial lining is the tissue that lines the uterus. Its thickness is measured by ultrasound and indicates readiness for embryo implantation.",
    whyItMatters:
      "A thick, trilaminar lining (triple-stripe pattern) is associated with better implantation rates. Thin lining may delay transfer.",
    normalRange:
      "Optimal thickness for transfer is typically ≥7–8 mm with a trilaminar pattern.",
  },
  "Follicle Count (AFC)": {
    description:
      "Antral follicle count (AFC) is the number of small follicles visible on ultrasound at the start of a cycle. It reflects ovarian reserve.",
    whyItMatters:
      "AFC helps predict how you'll respond to stimulation. A higher count usually means more eggs can be retrieved.",
    normalRange:
      "Normal AFC is 10–20 total (both ovaries). Below 5–6 suggests diminished reserve; above 20 may indicate PMOS (ex PCOS).",
  },
  AMH: {
    description:
      "Anti-Müllerian hormone (AMH) is produced by small ovarian follicles and reflects the size of the remaining egg supply (ovarian reserve).",
    whyItMatters:
      "AMH is the best single predictor of how many eggs you'll retrieve. It helps your clinic choose the right stimulation protocol and medication dose.",
    normalRange: AMH_NORMAL_RANGE,
    normalRangeBullets: AMH_NORMAL_RANGE_BULLETS,
    biology:
      "AMH is a glycoprotein made by granulosa cells in small, growing ovarian follicles—especially preantral and small antral follicles. There is not a fixed amount released by each follicle: the blood level is the combined signal from many follicles, their stage of growth, and the assay used by the laboratory.",
    measurementDetails:
      "A blood sample is separated into serum and analyzed with an AMH immunoassay. The assay detects AMH molecules and reports a concentration, usually in ng/mL. Results from different assays are not perfectly interchangeable, so serial results are most useful when compared with the same laboratory method and its assay-specific reference interval.",
    biologicalVariation:
      "AMH generally declines over reproductive life as the pool of growing follicles becomes smaller. It is usually more consistent across the menstrual cycle than FSH or estradiol, which is why it can be measured on most cycle days, but it is not static: hormonal contraception, ovarian surgery, PCOS-related follicle number, illness, and differences between assays can shift the result.",
  },
  "Anti-Mullerian Hormone": {
    description:
      "Anti-Müllerian hormone (AMH) is produced by small ovarian follicles and reflects the size of the remaining egg supply (ovarian reserve).",
    whyItMatters:
      "AMH is the best single predictor of how many eggs you'll retrieve. It helps your clinic choose the right stimulation protocol and medication dose.",
    normalRange: AMH_NORMAL_RANGE,
    normalRangeBullets: AMH_NORMAL_RANGE_BULLETS,
  },
  FSH: {
    description:
      "Follicle-stimulating hormone (FSH) is produced by the pituitary gland and drives follicle growth. Baseline FSH on cycle day 2–3 reflects ovarian reserve.",
    whyItMatters:
      "Elevated baseline FSH means the body is working harder to stimulate the ovaries, suggesting diminished reserve. It influences protocol selection.",
    normalRange:
      "Day 2–4 baseline: <10 IU/L normal. 10–15 IU/L: borderline. >15 IU/L: flags diminished ovarian reserve (ESHRE Bologna criteria 2011). FSH alone is not diagnostic — clinics combine it with AMH and antral follicle count.",
  },
  Progesterone: {
    description:
      "Progesterone is produced by the corpus luteum after ovulation. During IVF, it's critical for preparing the uterine lining for implantation.",
    whyItMatters:
      "Rising progesterone during stim can indicate premature luteinization, which may affect embryo transfer timing. After transfer, supplemental progesterone supports the lining.",
    normalRange:
      "Follicular (before ovulation): <1 ng/mL expected. Mid-luteal: ≥3 ng/mL confirms ovulation (ASRM 2021). FET / luteal target: ≥10 ng/mL supports implantation (Labarta 2017). During stim: should stay <1.5 ng/mL — higher suggests premature luteinization.",
  },
  TSH: {
    description:
      "Thyroid-stimulating hormone (TSH) reflects thyroid function. Thyroid health directly affects fertility, implantation, and early pregnancy.",
    whyItMatters:
      "Both hypothyroidism and hyperthyroidism can impair ovulation and increase miscarriage risk. Most reproductive endocrinologists want TSH optimized before treatment.",
    normalRange:
      "Optimal for fertility: 0.5–2.5 mIU/L. General normal range is 0.4–4.0 mIU/L, but fertility specialists prefer the tighter range.",
  },
  Testosterone: {
    description:
      "Testosterone is an androgen hormone present in both sexes. In females, elevated levels can indicate PMOS (ex PCOS) or adrenal disorders.",
    whyItMatters:
      "High testosterone can disrupt ovulation and follicle development. In PMOS (ex PCOS), it contributes to irregular cycles and may affect egg quality.",
    normalRange:
      "Female normal: 15–70 ng/dL. Elevated levels (>70) may warrant further investigation for PMOS (ex PCOS) or adrenal causes.",
  },
  "DHEA-S": {
    description:
      "DHEA-sulfate is an androgen precursor produced by the adrenal glands. It's sometimes supplemented to improve ovarian response in diminished reserve.",
    whyItMatters:
      "Low DHEA-S may indicate reduced ovarian reserve. Some clinics prescribe DHEA supplementation (75 mg/day for 6–12 weeks) before IVF to improve egg quality.",
    normalRange:
      "Normal: 35–430 µg/dL (varies by age). Levels below 100 µg/dL may prompt supplementation discussion.",
  },
  DHEA: {
    description:
      "DHEA-sulfate is an androgen precursor produced by the adrenal glands. It's sometimes supplemented to improve ovarian response in diminished reserve.",
    whyItMatters:
      "Low DHEA-S may indicate reduced ovarian reserve. Some clinics prescribe DHEA supplementation (75 mg/day for 6–12 weeks) before IVF to improve egg quality.",
    normalRange:
      "Normal: 35–430 µg/dL (varies by age). Levels below 100 µg/dL may prompt supplementation discussion.",
  },
  Prolactin: {
    description:
      "Prolactin is a pituitary hormone primarily associated with breast milk production. Elevated levels (hyperprolactinemia) can disrupt ovulation.",
    whyItMatters:
      "High prolactin suppresses GnRH, which reduces FSH and LH — leading to irregular or absent periods. It's treatable with medication (cabergoline or bromocriptine).",
    normalRange:
      "Normal: 2–29 ng/mL (non-pregnant). Levels >30 ng/mL may require treatment before starting IVF.",
  },
  "Beta-hCG": {
    description:
      "Beta-hCG (human chorionic gonadotropin) is the pregnancy hormone. It's produced by the developing placenta after implantation.",
    whyItMatters:
      "The beta-hCG blood test is the definitive pregnancy test after embryo transfer. Rising levels that double every 48–72 hours indicate a healthy early pregnancy.",
    normalRange:
      "Positive: >5 mIU/mL (most clinics use >25 as clinical positive). At 14 days post-transfer: typically 50–300 mIU/mL. Doubling time matters more than absolute number.",
  },
  hCG: {
    description:
      "Beta-hCG (human chorionic gonadotropin) is the pregnancy hormone. It's produced by the developing placenta after implantation.",
    whyItMatters:
      "The beta-hCG blood test is the definitive pregnancy test after embryo transfer. Rising levels that double every 48–72 hours indicate a healthy early pregnancy.",
    normalRange:
      "Positive: >5 mIU/mL (most clinics use >25 as clinical positive). At 14 days post-transfer: typically 50–300 mIU/mL. Doubling time matters more than absolute number.",
  },
};

// The content registry localizes linked test metadata, but the result
// factsheet also carries sourced patient education that does not live in a
// content block. Keep those translations beside the canonical factsheet so
// the same copy is used by Home, All Results, and Cycle Tests.
const BIOMARKER_FACTSHEET_FR: Record<string, BiomarkerFactsheetCopy> = {
  "Estradiol (E2)": {
    description:
      "L’estradiol (E2) est le principal œstrogène produit par les follicules ovariens en croissance. C’est l’hormone la plus suivie pendant la stimulation.",
    whyItMatters:
      "La hausse de l’estradiol confirme que les follicules grandissent et répondent au traitement. La clinique l’utilise avec l’échographie pour décider du moment du déclenchement.",
    normalRange:
      "Début de cycle (J2–4) : <80 pg/mL. Pendant la stimulation : 200–4000 pg/mL typiques (environ 200–300 par follicule mature). >4000 signale un risque de SHO (ESHRE 2019, Bosch 2003).",
  },
  E2: {
    description:
      "L’estradiol (E2) est le principal œstrogène produit par les follicules ovariens en croissance. C’est l’hormone la plus suivie pendant la stimulation.",
    whyItMatters:
      "La hausse de l’estradiol confirme que les follicules grandissent et répondent au traitement. La clinique l’utilise avec l’échographie pour décider du moment du déclenchement.",
    normalRange:
      "Début de cycle (J2–4) : <80 pg/mL. Pendant la stimulation : 200–4000 pg/mL typiques (environ 200–300 par follicule mature). >4000 signale un risque de SHO (ESHRE 2019, Bosch 2003).",
  },
  Estradiol: {
    description:
      "L’estradiol (E2) est le principal œstrogène produit par les follicules ovariens en croissance. C’est l’hormone la plus suivie pendant la stimulation.",
    whyItMatters:
      "La hausse de l’estradiol confirme que les follicules grandissent et répondent au traitement. La clinique l’utilise avec l’échographie pour décider du moment du déclenchement.",
    normalRange:
      "Début de cycle (J2–4) : <80 pg/mL. Pendant la stimulation : 200–4000 pg/mL typiques (environ 200–300 par follicule mature). >4000 signale un risque de SHO (ESHRE 2019, Bosch 2003).",
  },
  LH: {
    description:
      "L’hormone lutéinisante (LH) déclenche l’ovulation. Pendant une FIV, la clinique surveille la LH pour éviter un pic prématuré avant le prélèvement des ovocytes.",
    whyItMatters:
      "Un pic prématuré de LH peut provoquer une ovulation trop tôt et interrompre le cycle. Les antagonistes de la GnRH servent à le prévenir.",
    normalRange:
      "Début de cycle (J2–4) : 1–15 UI/L. Milieu du cycle (J12–16) : ≥15 = pic (ovulation dans les 24–36 h). Phase lutéale (J17+) : <15 typique. Une LH élevée avec un rapport LH/FSH >2 peut évoquer un profil de SOPK (ASRM 2022, Rotterdam 2003).",
  },
  "Endometrial Thickness": {
    description:
      "L’endomètre est le tissu qui tapisse l’intérieur de l’utérus. Son épaisseur est mesurée par échographie pour évaluer les conditions d’une implantation embryonnaire.",
    whyItMatters:
      "Un endomètre épais avec un aspect en trois feuillets est associé à de meilleurs taux d’implantation. Une muqueuse fine peut conduire à reporter le transfert.",
    normalRange:
      "Pour un transfert, une épaisseur d’au moins 7–8 mm avec un aspect en trois feuillets est généralement recherchée.",
  },
  "Follicle Count (AFC)": {
    description:
      "Le comptage des follicules antraux (CFA) correspond au nombre de petits follicules visibles à l’échographie au début du cycle. Il donne une indication de la réserve ovarienne.",
    whyItMatters:
      "Le CFA aide à prévoir la réponse à la stimulation. Un nombre plus élevé signifie généralement que davantage d’ovocytes peuvent être recueillis.",
    normalRange:
      "Un CFA total de 10 à 20 (les deux ovaires) est souvent considéré comme moyen. Moins de 5–6 peut évoquer une réserve diminuée ; plus de 20 peut évoquer un profil de PMOS (ex SOPK).",
  },
  AMH: {
    description:
      "L’hormone anti-müllérienne (AMH) est produite par les petits follicules ovariens et reflète la taille du stock d’ovocytes restant (réserve ovarienne).",
    whyItMatters:
      "L’AMH est le meilleur marqueur unique du nombre d’ovocytes qui pourraient être recueillis. Elle aide la clinique à choisir le protocole de stimulation et la dose de traitement.",
    normalRange:
      "Dépend de l’âge. Il n’existe pas une seule plage normale pour tout le monde ; compare ton résultat à la plage de référence adaptée à ton âge et au dosage du laboratoire qui a réalisé l’analyse.",
    normalRangeBullets: [
      "Médianes approximatives (dosage Beckman) : 25 ans ~3,3 ; 30 ans ~2,5 ; 35 ans ~1,4 ; 40 ans ~0,5 ; 42 ans ~0,3 ng/mL.",
      "Roche Elecsys peut donner des valeurs environ 23 % plus basses que Beckman.",
      "Compare ton résultat à la plage de référence adaptée à ton âge et au dosage du laboratoire qui a réalisé l’analyse.",
    ],
    biology:
      "L’AMH est une glycoprotéine produite par les cellules de la granulosa des petits follicules ovariens en croissance, notamment les follicules préantraux et les petits follicules antraux. Le taux sanguin correspond au signal combiné du nombre de follicules, de leur stade de croissance et du dosage utilisé par le laboratoire.",
    measurementDetails:
      "Un prélèvement sanguin est séparé en sérum puis analysé avec un immunodosage de l’AMH. Le dosage détecte les molécules d’AMH et indique une concentration, généralement en ng/mL. Les dosages ne sont pas parfaitement interchangeables ; les résultats successifs sont donc plus utiles lorsqu’ils utilisent la même méthode et la même plage de référence.",
    biologicalVariation:
      "L’AMH diminue généralement au cours de la vie reproductive lorsque le stock de follicules en croissance diminue. Elle varie souvent moins pendant le cycle que la FSH ou l’estradiol, mais elle n’est pas fixe : contraception hormonale, chirurgie ovarienne, profil de SOPK, maladie et différences entre dosages peuvent modifier le résultat.",
  },
  "Anti-Mullerian Hormone": {
    description:
      "L’hormone anti-müllérienne (AMH) est produite par les petits follicules ovariens et reflète la taille du stock d’ovocytes restant (réserve ovarienne).",
    whyItMatters:
      "L’AMH est le meilleur marqueur unique du nombre d’ovocytes qui pourraient être recueillis. Elle aide la clinique à choisir le protocole de stimulation et la dose de traitement.",
    normalRange:
      "Dépend de l’âge. Il n’existe pas une seule plage normale pour tout le monde ; compare ton résultat à la plage de référence adaptée à ton âge et au dosage du laboratoire qui a réalisé l’analyse.",
    normalRangeBullets: [
      "Médianes approximatives (dosage Beckman) : 25 ans ~3,3 ; 30 ans ~2,5 ; 35 ans ~1,4 ; 40 ans ~0,5 ; 42 ans ~0,3 ng/mL.",
      "Roche Elecsys peut donner des valeurs environ 23 % plus basses que Beckman.",
      "Compare ton résultat à la plage de référence adaptée à ton âge et au dosage du laboratoire qui a réalisé l’analyse.",
    ],
  },
  FSH: {
    description:
      "La FSH est produite par l’hypophyse et stimule la croissance des follicules. La FSH de début de cycle, mesurée aux jours 2–3, donne une indication de la réserve ovarienne.",
    whyItMatters:
      "Une FSH de début de cycle élevée signifie que le corps doit fournir davantage d’efforts pour stimuler les ovaires, ce qui peut évoquer une réserve diminuée. Elle aide à choisir le protocole.",
    normalRange:
      "Début de cycle (J2–4) : <10 UI/L normal. 10–15 UI/L : limite. >15 UI/L : peut évoquer une réserve ovarienne diminuée (critères de Bologne de l’ESHRE, 2011). La FSH seule ne suffit pas pour poser un diagnostic : la clinique l’associe à l’AMH et au CFA.",
  },
  Progesterone: {
    description:
      "La progestérone est produite par le corps jaune après l’ovulation. En FIV, elle joue un rôle essentiel dans la préparation de l’endomètre à l’implantation.",
    whyItMatters:
      "Une hausse de la progestérone pendant la stimulation peut signaler une lutéinisation précoce et modifier le moment du transfert. Après le transfert, un complément soutient l’endomètre.",
    normalRange:
      "Phase folliculaire (avant l’ovulation) : <1 ng/mL attendu. Milieu de la phase lutéale : ≥3 ng/mL confirme l’ovulation (ASRM 2021). Objectif en TEC / phase lutéale : ≥10 ng/mL pour soutenir l’implantation (Labarta 2017). Pendant la stimulation : <1,5 ng/mL attendu.",
  },
  TSH: {
    description:
      "La TSH reflète le fonctionnement de la thyroïde. La santé thyroïdienne influence directement la fertilité, l’implantation et le début de la grossesse.",
    whyItMatters:
      "L’hypothyroïdie comme l’hyperthyroïdie peuvent perturber l’ovulation et augmenter le risque de fausse couche. Les spécialistes cherchent généralement à optimiser la TSH avant le traitement.",
    normalRange:
      "Objectif fréquent en fertilité : 0,5–2,5 mUI/L. La plage générale est d’environ 0,4–4,0 mUI/L, mais les spécialistes de la fertilité utilisent souvent une plage plus étroite.",
  },
  Testosterone: {
    description:
      "La testostérone est une hormone androgène présente chez tous les sexes. Chez les femmes, un taux élevé peut évoquer un PMOS (ex SOPK) ou un trouble surrénalien.",
    whyItMatters:
      "Un taux élevé peut perturber l’ovulation et le développement folliculaire. Dans le PMOS (ex SOPK), il contribue aux cycles irréguliers et peut influencer la qualité ovocytaire.",
    normalRange:
      "Valeur habituelle chez la femme : 15–70 ng/dL. Un taux >70 peut nécessiter des examens complémentaires pour rechercher un PMOS (ex SOPK) ou une cause surrénalienne.",
  },
  "DHEA-S": {
    description:
      "La DHEA-S est un précurseur des androgènes produit par les glandes surrénales. Elle est parfois utilisée comme complément lorsque la réserve ovarienne est diminuée.",
    whyItMatters:
      "Une DHEA-S basse peut être associée à une réserve ovarienne diminuée. Certaines cliniques discutent une supplémentation avant la FIV pour soutenir la réponse ovarienne.",
    normalRange:
      "Valeur habituelle : 35–430 µg/dL (varie selon l’âge). Une valeur <100 µg/dL peut conduire à discuter une supplémentation.",
  },
  DHEA: {
    description:
      "La DHEA-S est un précurseur des androgènes produit par les glandes surrénales. Elle est parfois utilisée comme complément lorsque la réserve ovarienne est diminuée.",
    whyItMatters:
      "Une DHEA-S basse peut être associée à une réserve ovarienne diminuée. Certaines cliniques discutent une supplémentation avant la FIV pour soutenir la réponse ovarienne.",
    normalRange:
      "Valeur habituelle : 35–430 µg/dL (varie selon l’âge). Une valeur <100 µg/dL peut conduire à discuter une supplémentation.",
  },
  Prolactin: {
    description:
      "La prolactine est une hormone hypophysaire surtout connue pour son rôle dans la production de lait. Un taux élevé peut perturber l’ovulation.",
    whyItMatters:
      "Une prolactine élevée inhibe la GnRH, ce qui réduit la FSH et la LH et peut provoquer des règles irrégulières ou absentes. Elle peut être traitée par médicament.",
    normalRange:
      "Valeur habituelle : 2–29 ng/mL hors grossesse. Une valeur >30 ng/mL peut nécessiter une prise en charge avant une FIV.",
  },
  "Beta-hCG": {
    description:
      "La bêta-hCG est l’hormone de grossesse. Elle est produite par le placenta en développement après l’implantation.",
    whyItMatters:
      "La prise de sang de bêta-hCG est le test de grossesse de référence après un transfert embryonnaire. Une hausse qui double en 48–72 heures est généralement rassurante au début de la grossesse.",
    normalRange:
      "Positif : >5 mUI/mL (de nombreuses cliniques utilisent >25 comme seuil clinique). À 14 jours du transfert : souvent 50–300 mUI/mL. La progression compte davantage qu’un chiffre isolé.",
  },
  hCG: {
    description:
      "La bêta-hCG est l’hormone de grossesse. Elle est produite par le placenta en développement après l’implantation.",
    whyItMatters:
      "La prise de sang de bêta-hCG est le test de grossesse de référence après un transfert embryonnaire. Une hausse qui double en 48–72 heures est généralement rassurante au début de la grossesse.",
    normalRange:
      "Positif : >5 mUI/mL (de nombreuses cliniques utilisent >25 comme seuil clinique). À 14 jours du transfert : souvent 50–300 mUI/mL. La progression compte davantage qu’un chiffre isolé.",
  },
};

export function getBiomarkerFactsheet(
  markerName: string,
  locale: Locale = "en",
): BiomarkerFactsheet | null {
  let factsheetKey: string | undefined = BIOMARKER_FACTSHEETS[markerName]
    ? markerName
    : undefined;
  let factsheet = factsheetKey ? BIOMARKER_FACTSHEETS[factsheetKey] : undefined;

  if (!factsheet) {
    const normalizedMarkerName = normalizeBiomarkerName(markerName);
    for (const [key, value] of Object.entries(BIOMARKER_FACTSHEETS)) {
      const normalizedKey = normalizeBiomarkerName(key);
      if (normalizedKey === normalizedMarkerName) {
        factsheetKey = key;
        factsheet = value;
        break;
      }
      if (
        normalizedMarkerName.includes(normalizedKey) ||
        normalizedKey.includes(normalizedMarkerName)
      ) {
        factsheetKey = key;
        factsheet = value;
        break;
      }
    }
  }

  if (!factsheet) return null;

  const localizedCopy =
    locale === "fr" && factsheetKey
      ? BIOMARKER_FACTSHEET_FR[factsheetKey]
      : undefined;
  const localizedFactsheet = localizedCopy
    ? { ...factsheet, ...localizedCopy }
    : factsheet;

  const dataPoint = getDataPointForMarkerName(markerName);
  if (!dataPoint) return localizedFactsheet;

  const dataPointBody = dataPoint.body;
  const linkedTests = getTestsForDataPoint(dataPoint.id);
  const testingMethods = linkedTests.map((test) => {
    const testBody = test.body;
    const sampleTypes =
      dataPointBody.sampleTypes?.length
        ? dataPointBody.sampleTypes
        : [];
    return {
      testId: test.id,
      title: test.title,
      subtitle: test.subtitle,
      sampleTypes,
      collectionLabel: getCollectionLabel(testBody),
      timing: testBody.cycleDay,
      metricKey: dataPointBody.metricKey,
    } satisfies BiomarkerTestingMethod;
  });

  const relatedMarkers = Array.from(
    new Set(
      linkedTests.flatMap((test) => {
        return getDataPointsForTest(test.id)
          .filter((block) => block.id !== dataPoint.id)
          .map((block) => block.title);
      }),
    ),
  );
  // Use the focused single-marker sheet for the biomarker's core copy when
  // one exists. Keep relatedMarkers derived from all linked tests so package
  // relationships remain visible until we give them an editorial taxonomy.
  const primaryTest =
    linkedTests.find((test) => getDataPointsForTest(test.id).length === 1) ??
    linkedTests[0];
  const primaryTestBody = primaryTest?.body;

  return {
    ...localizedFactsheet,
    subtitle: primaryTest?.subtitle ?? localizedFactsheet.subtitle,
    tags: primaryTest?.tags ?? dataPoint.tags ?? localizedFactsheet.tags,
    icon: primaryTest?.icon ?? localizedFactsheet.icon,
    sampleTypes:
      dataPointBody.sampleTypes ?? localizedFactsheet.sampleTypes,
    testingMethods,
    // `whatItMeasures` belongs in the plain-language Overview. Keep the
    // Biology tab reserved for explicitly technical content instead of
    // duplicating the patient-facing definition there.
    description:
      localizedCopy?.description ??
      primaryTestBody?.whatItMeasures ??
      localizedFactsheet.description,
    biology: localizedCopy?.biology ?? localizedFactsheet.biology,
    measurementDetails:
      localizedCopy?.measurementDetails ??
      localizedFactsheet.measurementDetails ??
      primaryTestBody?.howItWorks,
    biologicalVariation:
      localizedCopy?.biologicalVariation ?? localizedFactsheet.biologicalVariation,
    interpretation:
      dataPointBody.interpretation ?? localizedFactsheet.interpretation,
    howItWorks: primaryTestBody?.howItWorks ?? localizedFactsheet.howItWorks,
    testingTiming:
      primaryTestBody?.cycleDay ?? localizedFactsheet.testingTiming,
    relatedMarkers,
  };
}

export type { BiomarkerFactsheet };
